import React, { useState, useEffect } from 'react';
import './OrdersTable.css';
import Pagination from './Pagination';
import GatewayDetailsPanel from './GatewayDetailsPanel';
import { fetchTradersData } from '../services/dataApi';
import useUrlDetails from '../hooks/useUrlDetails';

const GatewaysTable = ({
  data,
  showTraderName = false,
  pagination,
  onPageChange,
  onAddBalance,
  balanceRequestLoadingRef
}) => {
  const [traders, setTraders] = useState([]);
  const [tradersLoading, setTradersLoading] = useState(false);

  // Load the trader list if the trader name needs to be shown
  useEffect(() => {
    if (showTraderName) {
      const loadTraders = async () => {
        setTradersLoading(true);
        try {
          // Load all traders (large limit to fetch all of them)
          const tradersData = await fetchTradersData(1, 1000);
          let tradersList = [];
          
          // Support the new paginated format
          if (tradersData && tradersData.data && Array.isArray(tradersData.data)) {
            tradersList = tradersData.data;
          } else if (Array.isArray(tradersData)) {
            tradersList = tradersData;
          } else if (tradersData && tradersData.ops && tradersData.ops.length > 0) {
            const firstOp = tradersData.ops[0];
            if (firstOp.list && Array.isArray(firstOp.list)) {
              tradersList = firstOp.list;
            } else if (firstOp.data && firstOp.data.list && Array.isArray(firstOp.data.list)) {
              tradersList = firstOp.data.list;
            }
          }
          setTraders(tradersList);
        } catch (err) {
          console.error('Error loading traders:', err);
          setTraders([]);
        } finally {
          setTradersLoading(false);
        }
      };
      
      loadTraders();
    }
  }, [showTraderName]);

  // Function to get the trader name by traderId
  const getTraderNameById = (traderId) => {
    if (!traderId) return '-';
    const trader = traders.find(t => {
      const id = t.traderId || t.TraderId || t.TRADER_ID || t.ref;
      return id === traderId;
    });
    if (trader) {
      return trader.name || trader.Name || trader.NAME || trader.login || trader.Login || traderId;
    }
    return traderId;
  };

  // Extract data - support both the new paginated format and the old format
  let gateways = [];
  
  if (data && data.data && Array.isArray(data.data)) {
    // New paginated format
    gateways = data.data;
  } else if (Array.isArray(data)) {
    // Old format - just an array
    gateways = data;
  } else if (data && data.ops && data.ops.length > 0) {
    // Backward compatibility with the old structure
    const firstOp = data.ops[0];
    if (firstOp.list && Array.isArray(firstOp.list)) {
      gateways = firstOp.list;
    } else if (firstOp.data && firstOp.data.list && Array.isArray(firstOp.data.list)) {
      gateways = firstOp.data.list;
    }
  }

  const [selectedGateway, setSelectedGateway] = useUrlDetails(gateways, 'gatewayId');

  // Sort by creation date (create_time) - newest first
  gateways.sort((a, b) => {
    const timeA = a.create_time || 0;
    const timeB = b.create_time || 0;
    return timeB - timeA;
  });

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    try {
      const date = new Date(timestamp * 1000); // If timestamp is in seconds
      if (isNaN(date.getTime())) {
        const date2 = new Date(timestamp); // Try as milliseconds
        if (isNaN(date2.getTime())) return String(timestamp);
        return formatCustomDate(date2);
      }
      return formatCustomDate(date);
    } catch {
      return String(timestamp);
    }
  };

  const formatCustomDate = (date) => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    return (
      <div className="gateways-date-cell">
        <span>{day}.{month}.{year}</span>
        <span>{hours}:{minutes}:{seconds}</span>
      </div>
    );
  };

  // Determine the columns to display
  const displayColumns = [
    { 
      key: 'Created', 
      label: 'Created', 
      getValue: (gateway) => {
        const created = gateway.create_time || null;
        return formatDate(created);
      }
    },
    ...(showTraderName ? [{
      key: 'TraderName',
      label: 'Trader Name',
      getValue: (gateway) => {
        const traderId = gateway.traderId || gateway.TraderId || gateway.TRADER_ID;
        return tradersLoading ? 'Loading...' : getTraderNameById(traderId);
      }
    }] : []),
    { 
      key: 'Name', 
      label: 'Name', 
      getValue: (gateway) => {
        return gateway.name || gateway.Name || gateway.NAME || '-';
      }
    },
    { 
      key: 'Id', 
      label: 'ID', 
      getValue: (gateway) => {
        return gateway.id || gateway.Id || gateway.ID || '-';
      }
    },
    { 
      key: 'Currency', 
      label: 'Currency', 
      getValue: (gateway) => {
        return gateway.currency || gateway.Currency || gateway.CURRENCY || '-';
      }
    },
    { 
      key: 'Geo', 
      label: 'Geo', 
      getValue: (gateway) => {
        return gateway.geo || gateway.Geo || gateway.GEO || '-';
      }
    },
    { 
      key: 'Method', 
      label: 'Method', 
      getValue: (gateway) => {
        return gateway.method || gateway.Method || gateway.METHOD || '-';
      }
    },
    { 
      key: 'Balance', 
      label: 'Balance', 
      getValue: (gateway) => {
        const balance = gateway.balance || gateway.Balance || gateway.BALANCE;
        const gatewayRef = gateway.ref || gateway.gatewayId || gateway.GatewayId || gateway.GATEWAY_ID || gateway.id || gateway.Id || gateway.ID || '';
        const isLoading = balanceRequestLoadingRef === gatewayRef;
        const addButton = onAddBalance ? (
          <button
            type="button"
            className="inline-add-button"
            disabled={isLoading}
            onClick={(event) => {
              event.stopPropagation();
              onAddBalance(gateway);
            }}
          >
            {isLoading ? 'Adding...' : 'Add'}
          </button>
        ) : null;

        if (balance !== null && typeof balance === 'object') {
          const current = balance.current || balance.Current || balance.CURRENT || '0';
          const pending = balance.pending || balance.Pending || balance.PENDING || '0';
          const total = balance.total || balance.Total || balance.TOTAL || '0';
          return (
            <div className="gateway-balance-cell">
              <span className="gateway-balance-value"><small>Current</small>{current} USDT</span>
              <span className="gateway-balance-value"><small>Pending</small>{pending} USDT</span>
              <span className="gateway-balance-value"><small>Total</small>{total} USDT</span>
              {addButton}
            </div>
          );
        }

        return (
          <div className="gateway-balance-cell">
            <span>{balance === null || balance === undefined ? '0' : String(balance)}</span>
            {addButton}
          </div>
        );
      }
    },
    { 
      key: 'PayInCoefficient', 
      label: 'PayIN Coeff', 
      getValue: (gateway) => {
        return gateway.payInCoefficient || gateway.PayInCoefficient || gateway.PAYIN_COEFFICIENT || '-';
      }
    },
    { 
      key: 'PayOutCoefficient', 
      label: 'PayOUT Coeff', 
      getValue: (gateway) => {
        return gateway.payOutCoefficient || gateway.PayOutCoefficient || gateway.PAYOUT_COEFFICIENT || '-';
      }
    }
  ];

  if (gateways.length === 0) {
    return (
      <div className="orders-table-container">
        <div className="orders-header">
          <h3>Gateways List (0)</h3>
        </div>
        <div className="page-placeholder">
          <p>No gateways found</p>
        </div>
      </div>
    );
  }

  const totalItems = pagination?.total || gateways.length;

  return (
    <>
      <div className="orders-table-container">
        <div className="orders-header">
          <h3>Gateways List ({totalItems})</h3>
        </div>
        <div className="table-wrapper">
          <table className="orders-table gateways-mobile-table">
            <thead>
              <tr>
                {displayColumns.map((column) => (
                  <th key={column.key} className={`header-${column.key.toLowerCase()}`}>
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {gateways.map((gateway, index) => {
                const status = (gateway.status || gateway.Status || gateway.STATUS || '').toLowerCase();
                return (
                  <tr 
                    key={gateway.ref || gateway.id || index}
                    className="order-row"
                    onClick={() => setSelectedGateway(gateway)}
                  >
                    {displayColumns.map((column) => {
                      const isStatusColumn = column.key === 'Status';
                      const statusClass = isStatusColumn ? `status-${status}` : '';
                      return (
                        <td 
                          key={column.key} 
                          className={`column-${column.key.toLowerCase()} ${statusClass}`}
                          data-label={column.label}
                        >
                          {column.getValue(gateway)}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {pagination && onPageChange && (
          <Pagination
            currentPage={pagination.page}
            totalPages={pagination.totalPages}
            onPageChange={onPageChange}
            itemsPerPage={pagination.limit}
            totalItems={pagination.total}
          />
        )}
      </div>
      {selectedGateway && (
        <GatewayDetailsPanel 
          gateway={selectedGateway} 
          onClose={() => setSelectedGateway(null)} 
        />
      )}
    </>
  );
};

export default GatewaysTable;
